import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { Toast as ToastPrimitive } from 'radix-ui'
import { CheckCircle2, Info, TriangleAlert, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

type Tone = 'success' | 'info' | 'danger'
interface ToastItem {
  id: number
  tone: Tone
  title: string
  description?: string | undefined
}

interface ToastApi {
  show: (toast: Omit<ToastItem, 'id' | 'tone'> & { tone?: Tone }) => void
}

const ToastContext = createContext<ToastApi>({ show: () => undefined })

export function useToast() {
  return useContext(ToastContext)
}

const toneStyles: Record<Tone, { className: string; Icon: typeof Info }> = {
  success: { className: 'border-success bg-success-bg text-success', Icon: CheckCircle2 },
  info: { className: 'border-info bg-info-bg text-info', Icon: Info },
  danger: { className: 'border-danger bg-danger-bg text-danger', Icon: TriangleAlert },
}

/** Toasts confirm simple actions. They are never the only place for a critical error. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const [items, setItems] = useState<ToastItem[]>([])
  const show = useCallback<ToastApi['show']>((toast) => {
    setItems((current) => [...current.slice(-2), { id: Date.now() + Math.random(), tone: toast.tone ?? 'success', title: toast.title, description: toast.description }])
  }, [])
  const api = useMemo(() => ({ show }), [show])

  return (
    <ToastContext.Provider value={api}>
      <ToastPrimitive.Provider duration={5000} label={t('common.notifications')}>
        {children}
        {items.map((item) => {
          const { className, Icon } = toneStyles[item.tone]
          return (
            <ToastPrimitive.Root
              key={item.id}
              onOpenChange={(open) => {
                if (!open) setItems((current) => current.filter((i) => i.id !== item.id))
              }}
              className={cn('flex items-start gap-3 rounded-card border p-4 shadow-float', className)}
            >
              <Icon aria-hidden className="mt-0.5 size-5 shrink-0" />
              <div className="min-w-0 flex-1">
                <ToastPrimitive.Title className="text-body font-semibold">{item.title}</ToastPrimitive.Title>
                {item.description ? (
                  <ToastPrimitive.Description className="mt-1 text-small">{item.description}</ToastPrimitive.Description>
                ) : null}
              </div>
              <ToastPrimitive.Close aria-label={t('common.close')} className="-m-2 inline-flex size-11 items-center justify-center rounded-control">
                <X aria-hidden className="size-4" />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          )
        })}
        <ToastPrimitive.Viewport className="fixed inset-x-0 bottom-[calc(80px+env(safe-area-inset-bottom))] z-[60] mx-auto flex w-full max-w-md list-none flex-col gap-2 px-4 outline-none lg:bottom-4" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  )
}
